import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  MenuItem,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import SearchIcon from "@mui/icons-material/Search";
import api from '../../services/api';
const TrialImportPage = () => {
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [importResults, setImportResults] = useState(null);

  // Single trial import
  const [nctId, setNctId] = useState("");

  // Search import
  const [searchQuery, setSearchQuery] = useState("amyloidosis");
  const [searchStatus, setSearchStatus] = useState("RECRUITING");
  const [maxResults, setMaxResults] = useState(20);

  const handleSingleImport = async () => {
    if (!nctId.trim()) {
      setError("Please enter an NCT ID");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setImportResults(null);

      
      const response = await api.post(`/v1/admin/import/trial/${nctId}`);
      const data = response.data;
      setSuccess(`Trial ${nctId} imported successfully!`);
      setNctId("");
      console.log("Imported trial:", data.trial);
    } catch (err) {
      console.error("Error importing trial:", err);
      setError(err.message || "Failed to import trial");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchImport = async () => {
    if (!searchQuery.trim()) {
      setError("Please enter a search query");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setImportResults(null);

      //const token = await getAccessTokenSilently();
      const response = await api.post('/v1/admin/import/search', {
        query: searchQuery,
        status: searchStatus,
        maxResults: maxResults,
      });

      const data = response.data;

      setImportResults(data);
      setSuccess(`Imported ${data.successCount} trials successfully!`);
    } catch (err) {
      console.error("Error importing trials:", err);
      setError(err.message || "Failed to import trials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 4,
          background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
          color: "white",
        }}
      >
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Import Clinical Trials
        </Typography>
        <Typography variant="subtitle1">
          Import trials from ClinicalTrials.gov API
        </Typography>
      </Paper>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Single Trial Import */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CloudDownloadIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Import Single Trial</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Import a specific trial by NCT ID
              </Typography>

              <TextField
                fullWidth
                label="NCT ID"
                placeholder="NCT12345678"
                value={nctId}
                onChange={(e) => setNctId(e.target.value.toUpperCase())}
                sx={{ mb: 2 }}
                helperText="Enter the NCT ID from ClinicalTrials.gov"
              />

              <Button
                fullWidth
                variant="contained"
                startIcon={
                  loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CloudDownloadIcon />
                  )
                }
                onClick={handleSingleImport}
                disabled={loading || !nctId.trim()}
              >
                {loading ? "Importing..." : "Import Trial"}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Search Import */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SearchIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Import by Search</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Search and import multiple trials
              </Typography>

              <TextField
                fullWidth
                label="Search Query"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
                helperText="e.g., amyloidosis, AL amyloidosis"
              />

              <TextField
                fullWidth
                select
                label="Trial Status"
                value={searchStatus}
                onChange={(e) => setSearchStatus(e.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value="RECRUITING">Recruiting</MenuItem>
                <MenuItem value="ACTIVE_NOT_RECRUITING">
                  Active, Not Recruiting
                </MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="">All Statuses</MenuItem>
              </TextField>

              <TextField
                fullWidth
                type="number"
                label="Max Results"
                value={maxResults}
                onChange={(e) => setMaxResults(parseInt(e.target.value))}
                sx={{ mb: 2 }}
                inputProps={{ min: 1, max: 100 }}
              />

              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={
                  loading ? <CircularProgress size={20} /> : <SearchIcon />
                }
                onClick={handleSearchImport}
                disabled={loading || !searchQuery.trim()}
              >
                {loading ? "Importing..." : "Search & Import"}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Import Results */}
      {importResults && (
        <Paper elevation={2} sx={{ p: 3, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Import Results
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Chip
                label={`Total: ${importResults.successCount + importResults.failureCount
                  }`}
                color="default"
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Chip
                label={`Success: ${importResults.successCount}`}
                color="success"
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Chip
                label={`Failed: ${importResults.failureCount}`}
                color="error"
                sx={{ width: "100%" }}
              />
            </Grid>
          </Grid>

          {importResults.successfulTrials &&
            importResults.successfulTrials.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Successfully Imported:
                </Typography>
                <List dense>
                  {importResults.successfulTrials
                    .slice(0, 10)
                    .map((trial, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={trial.nctId}
                          secondary={trial.title || "No title"}
                        />
                      </ListItem>
                    ))}
                  {importResults.successfulTrials.length > 10 && (
                    <ListItem>
                      <ListItemText
                        secondary={`...and ${importResults.successfulTrials.length - 10
                          } more`}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

          {importResults.failures && importResults.failures.length > 0 && (
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color="error"
                gutterBottom
              >
                Failed Imports:
              </Typography>
              <List dense>
                {importResults.failures.map((failure, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={failure.nctId}
                      secondary={failure.error}
                      secondaryTypographyProps={{ color: "error" }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      )}

      {/* Quick Tips */}
      <Paper
        elevation={1}
        sx={{ p: 3, mt: 4, bgcolor: "info.light", color: "info.contrastText" }}
      >
        <Typography variant="h6" gutterBottom>
          💡 Quick Tips
        </Typography>
        <Typography variant="body2" component="div">
          <ul>
            <li>Single import: Use when you know the exact NCT ID</li>
            <li>Search import: Great for bulk importing chronic kidney disease trials</li>
            <li>Recommended query: "chronic kidney disease" with status "RECRUITING"</li>
            <li>
              After import, trials need manual curation for ckdStage and
              targetOrgan fields
            </li>
          </ul>
        </Typography>
      </Paper>
    </Container>
  );
};

export default TrialImportPage;
